import { AttachState } from '@fluidframework/container-definitions';
import { ActionDebouncer } from '../../../utilities/ActionDebouncer';
const connectionStateConnected = 2;
/**
 * How long we should wait once we are disconnected before notifying the user that they are disconnected.
 * If the user is active when we get disconnected, the wait starts right away.
 * If the user is inactive when we get disconnected, the wait starts when they become active again.
 */ const waitForDisconnectedNotifyInMs = 15000;
/**
 * Friendly API for deriving UX around connected state of the container. It has special logic to make sure
 * we show the right state on boot and that we don't show the disconnected UI too quickly in the event of returning
 * from a long idle period or in cases of intermittent connectivity loss.
 */ export class ContainerConnectionStateTracker {
    /**
   * Puts the tracker back into the initial state so that the listener can be brought back into a good state.
   * This is useful for scenarios like bringing the connection state back up to date after an intentional extended offline period.
   */ reset() {
        // Act as if the listener has not received any messages
        this.lastEmittedConnectionValue = undefined;
        this.scheduleConnectionMessage();
    }
    /**
   * Set the reason that caused the last disconnect
   * @param reason The reason for the last disconnect
   */ setLastDisconnectedReason(reason) {
        this.lastDisconnectedReason = reason;
    }
    /**
   * This API is used to explicitly send a disconnect/connect message to the listener so it can b
   */ scheduleConnectionMessage() {
        if (this.container.connectionState === connectionStateConnected) {
            this.onConnect();
        } else {
            this.onDisconnect();
        }
    }
    dispose() {
        this.container.off('connected', this.onConnect);
        this.container.off('disconnected', this.onDisconnect);
        this.disconnectDebouncer.dispose();
    }
    constructor(container, onConnectionStateChangeHandler){
        this.container = container;
        this.onConnectionStateChangeHandler = onConnectionStateChangeHandler;
        this.emitConnectionStateChange = (newState, disconnectedReason)=>{
            // Container only emits connection signals after attach
            if (this.container.attachState === AttachState.Attached && this.lastEmittedConnectionValue !== newState) {
                this.onConnectionStateChangeHandler(newState, newState === 'disconnected' ? disconnectedReason : undefined /* disconnectedReason */ , this.container.isDirty /* containerDirty */ , this.container.closed /* containerClosed */ );
            }
            this.lastEmittedConnectionValue = newState;
        };
        this.onConnect = ()=>{
            // If we reconnect before the disconnect timer fires, let's clear any pending (debounced) disconnects
            this.disconnectDebouncer.cancelPendingDebounce();
            this.emitConnectionStateChange('connected');
        };
        this.onDisconnect = (reason)=>{
            // We don't want to emit the disconnect event right away to the user, instead we give the container some time to connect
            this.lastDisconnectedReason = reason;
            this.disconnectDebouncer.startOrRefreshDebounce();
        };
        this.disconnectDebouncer = new ActionDebouncer(()=>this.emitConnectionStateChange('disconnected', this.lastDisconnectedReason), waitForDisconnectedNotifyInMs);
        container.on('connected', this.onConnect);
        container.on('disconnected', this.onDisconnect);
        this.scheduleConnectionMessage();
    }
} // cspell:ignore Debouncer
